import { useEffect, useMemo, useState, type FormEvent } from 'react'

import { api } from '../api/client'
import type { EagleApplicaEsito, EagleMonitorato, EagleProfilo, EagleProfiloDef, EagleRegolaProfilo, EagleVoceRegola } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from './common'
import { DataTable } from './DataTable'
import { etichettaFrequenza } from './eagleTesti'
import { Finestra } from './ModificheGitlab'

/**
 * Eagle → Profili (ADR-0022 §9): monitoring profiles — rules by parameter
 * kind — applied to many CIs at once from the CI list, or automatically to
 * the CIs that carry a tag. The same rules editor serves the ad-hoc
 * «Parametri…» of the CI list selection (`FinestraParametri`).
 */

/** The kinds a rule can name; instance kinds have many parameters per CI and take a «which». */
export const KINDS_PROFILO: { kind: string; label: () => string; istanza: boolean; unit: string }[] = [
  { kind: 'ping', label: () => t('Ping'), istanza: false, unit: '%' },
  { kind: 'ping_fast', label: () => t('Ping rapido'), istanza: false, unit: 'ms' },
  { kind: 'tcp', label: () => t('Porte TCP'), istanza: true, unit: 'ms' },
  { kind: 'cpu', label: () => t('CPU'), istanza: false, unit: '%' },
  { kind: 'memory', label: () => t('Memoria'), istanza: false, unit: '%' },
  { kind: 'volume', label: () => t('Volumi'), istanza: true, unit: '%' },
  { kind: 'interface', label: () => t('Interfacce'), istanza: true, unit: '%' },
  { kind: 'service', label: () => t('Servizi'), istanza: true, unit: '' },
  { kind: 'services_auto', label: () => t('Servizi automatici fermi'), istanza: false, unit: '' },
  { kind: 'service_group', label: () => t('Gruppi di servizi'), istanza: true, unit: '' },
  { kind: 'process', label: () => t('Processi'), istanza: true, unit: '' },
  { kind: 'uptime', label: () => t('Tempo dall’avvio'), istanza: false, unit: 'h' },
  { kind: 'disk_health', label: () => t('Salute dei dischi'), istanza: true, unit: '' },
  { kind: 'updates_pending', label: () => t('Aggiornamenti in sospeso'), istanza: false, unit: '' },
  { kind: 'reboot_required', label: () => t('Riavvio richiesto'), istanza: false, unit: '' },
  { kind: 'ups_battery', label: () => t('UPS: batteria'), istanza: false, unit: '%' },
  { kind: 'ups_runtime', label: () => t('UPS: autonomia'), istanza: false, unit: 'min' },
  { kind: 'ups_load', label: () => t('UPS: carico'), istanza: false, unit: '%' },
]

export const REGOLA_VUOTA: EagleRegolaProfilo = { kind: 'ping', enabled: true, match: 'all', names: [], items: [], interval_s: null, warn: 'default', crit: 'default' }

const MOTIVI: Record<string, () => string> = {
  no_credential: () => t('senza credenziale SNMP'),
  credential_probe: () => t('credenziale da provare'),
  no_source: () => t('nessuna fonte che lo legga'),
  no_address: () => t('senza indirizzo'),
  method_pending: () => t('metodo non ancora disponibile'),
  no_longer: () => t('non più in inventario'),
}

function etichettaKind(kind: string): string {
  return KINDS_PROFILO.find((k) => k.kind === kind)?.label() ?? kind
}

/** One line for a rule list: «ping · CPU · interfacce (del profilo) · servizi: sql*, w3svc · ✕ memoria». */
export function riassuntoRegole(regole: EagleRegolaProfilo[]): string {
  return regole.map((r) => {
    const nome = etichettaKind(r.kind)
    const nomi = [...(r.items ?? []).map((v) => v.name), ...r.names]
    const quali = r.match === 'named' ? `: ${nomi.join(', ')}` : r.match === 'profile' ? ` (${t('del profilo')})` : ''
    return `${r.enabled ? '' : '✕ '}${nome}${quali}`
  }).join(' · ')
}

/** The outcome of an application, in one sentence, with the reasons for what could not be read. */
export function testoEsito(e: EagleApplicaEsito): string {
  const parti = [t('{n} CI impostati, {p} parametri accesi', { n: e.changed, p: e.params })]
  if (e.skipped.length > 0) {
    parti.push(t('{m} saltati: {elenco}', { m: e.skipped.length, elenco: e.skipped.slice(0, 4).map((x) => `${x.name} (${x.reason})`).join(', ') + (e.skipped.length > 4 ? '…' : '') }))
  }
  const motivi = Object.entries(e.unreadable)
  if (motivi.length > 0) {
    parti.push(t('non leggibili: {elenco}', { elenco: motivi.map(([k, n]) => `${MOTIVI[k]?.() ?? k} ×${n}`).join(', ') }))
  }
  return parti.join(' · ')
}

/** The CI list can hold thousands of rows: the apply goes in lots of 500 and the outcomes add up. */
export async function applicaInLotti(ids: string[], body: { profile_id?: string; rules?: EagleRegolaProfilo[]; replace?: boolean; save_as?: string | null }): Promise<EagleApplicaEsito> {
  let totale: EagleApplicaEsito | null = null
  let corpo = body
  for (let i = 0; i < ids.length; i += 500) {
    const r = await api.eagleProfiloApplica({ ...corpo, ids: ids.slice(i, i + 500) })
    // Saved on the first lot: the next ones apply the profile just created.
    if (corpo.save_as && r.profile_id) corpo = { profile_id: r.profile_id }
    if (!totale) { totale = r; continue }
    const prima: EagleApplicaEsito = totale
    const nonLeggibili: Record<string, number> = { ...prima.unreadable }
    for (const [k, n] of Object.entries(r.unreadable)) nonLeggibili[k] = (nonLeggibili[k] ?? 0) + n
    totale = {
      ...prima, matched: prima.matched + r.matched, changed: prima.changed + r.changed, params: prima.params + r.params,
      skipped: [...prima.skipped, ...r.skipped], not_found: prima.not_found + r.not_found, unreadable: nonLeggibili,
    }
  }
  return totale ?? { licensed: true, matched: 0, changed: 0, params: 0, skipped: [], not_found: 0, unreadable: {}, profile_id: null, profile_name: null, by_tag: false }
}

// ---------------------------------------------------------- the editor

function Soglia({ etichetta, valore, unit, onCambia, testoPredefinita }: {
  etichetta: string; valore: number | null | 'default'; unit: string; onCambia: (v: number | null | 'default') => void; testoPredefinita?: string
}) {
  const modo = valore === 'default' ? 'default' : valore === null ? 'none' : 'value'
  return (
    <span className="eprof__soglia">
      <span className="muted">{etichetta}</span>
      <select aria-label={t('Soglia di {s}', { s: etichetta })} value={modo}
              onChange={(e) => onCambia(e.target.value === 'default' ? 'default' : e.target.value === 'none' ? null : (typeof valore === 'number' ? valore : 0))}>
        <option value="default">{testoPredefinita ?? t('predefinita')}</option>
        <option value="none">{t('nessuna')}</option>
        <option value="value">{t('valore')}</option>
      </select>
      {modo === 'value' && (
        <>
          <input type="number" inputMode="decimal" step="any" aria-label={t('Valore di {s}', { s: etichetta })} value={valore as number}
                 onChange={(e) => onCambia(e.target.value === '' ? 0 : Number(e.target.value))} style={{ width: 80 }} />
          {unit && <span className="muted">{unit}</span>}
        </>
      )}
    </span>
  )
}

/** The rules of a profile, one row each: kind, on/off, which, interval, thresholds. */
export function EditorRegole({ regole, onCambia, intervalli }: {
  regole: EagleRegolaProfilo[]; onCambia: (r: EagleRegolaProfilo[]) => void; intervalli: number[]
}) {
  const cambia = (i: number, patch: Partial<EagleRegolaProfilo>) => onCambia(regole.map((r, k) => (k === i ? { ...r, ...patch } : r)))
  return (
    <div className="eprof__regole">
      {regole.length === 0 && <p className="muted" style={{ margin: 0 }}>{t('Nessuna regola: aggiungine una.')}</p>}
      {regole.map((r, i) => {
        const def = KINDS_PROFILO.find((k) => k.kind === r.kind) ?? KINDS_PROFILO[0]
        return (
          <div key={i} className="eprof__regola">
            <select aria-label={t('Tipo di parametro')} value={r.kind}
                    onChange={(e) => cambia(i, { kind: e.target.value, match: 'all', names: [] })}>
              {KINDS_PROFILO.map((k) => <option key={k.kind} value={k.kind}>{k.label()}</option>)}
            </select>
            <select aria-label={t('Accendi o spegni')} value={r.enabled ? 'on' : 'off'} onChange={(e) => cambia(i, { enabled: e.target.value === 'on' })}>
              <option value="on">{t('accendi')}</option>
              <option value="off">{t('spegni')}</option>
            </select>
            {def.istanza && (
              <select aria-label={t('Quali')} value={r.match} onChange={(e) => cambia(i, { match: e.target.value as EagleRegolaProfilo['match'] })}>
                <option value="all">{t('tutti')}</option>
                <option value="profile">{t('quelli del profilo della classe')}</option>
                <option value="named">{t('per nome')}</option>
              </select>
            )}
            {def.istanza && r.match === 'named' && (
              <input type="text" aria-label={t('Nomi')} placeholder={t('nomi con * come jolly, separati da virgola (facoltativo)')} value={r.names.join(', ')}
                     onChange={(e) => cambia(i, { names: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} />
            )}
            {r.enabled && (
              <>
                <select aria-label={t('Frequenza')} value={r.interval_s ?? ''} onChange={(e) => cambia(i, { interval_s: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">{t('frequenza predefinita')}</option>
                  {intervalli.map((s) => <option key={s} value={s}>{etichettaFrequenza(s)}</option>)}
                </select>
                <Soglia etichetta={t('attenzione')} valore={r.warn} unit={def.unit} onCambia={(v) => cambia(i, { warn: v })} />
                <Soglia etichetta={t('critico')} valore={r.crit} unit={def.unit} onCambia={(v) => cambia(i, { crit: v })} />
              </>
            )}
            <button type="button" className="secondary eprof__togli" aria-label={t('Togli la regola')} title={t('Togli la regola')}
                    onClick={() => onCambia(regole.filter((_, k) => k !== i))}>✕</button>
            {def.istanza && r.match === 'named' && (
              <VociRegola kind={r.kind} unit={def.unit} voci={r.items ?? []} intervalli={intervalli} onCambia={(items) => cambia(i, { items })} />
            )}
          </div>
        )
      })}
      <div>
        <button type="button" className="secondary" onClick={() => onCambia([...regole, { ...REGOLA_VUOTA }])}>{t('Aggiungi una regola')}</button>
      </div>
    </div>
  )
}

// ------------------------------------------- the named instances of a rule

/**
 * The single instances of a «by name» rule — «Gi1/0/1 at 60/80 every
 * minute, Gi1/0/24 switched off» — one row each, with their own settings;
 * what a row leaves out takes the rule's. «Prendi da un CI…» fills the rows
 * from a real CI's catalogue, so nobody has to type interface names by
 * heart: the port description comes along as the label.
 */
function VociRegola({ kind, unit, voci, intervalli, onCambia }: {
  kind: string; unit: string; voci: EagleVoceRegola[]; intervalli: number[]; onCambia: (v: EagleVoceRegola[]) => void
}) {
  const [daCi, setDaCi] = useState(false)
  const cambia = (i: number, patch: Partial<EagleVoceRegola>) => onCambia(voci.map((v, k) => (k === i ? { ...v, ...patch } : v)))
  return (
    <div className="eprof__voci">
      {voci.map((v, i) => (
        <div key={i} className="eprof__voce">
          <input type="text" aria-label={t('Nome')} placeholder={t('nome (es. Gi1/0/1)')} value={v.name} onChange={(e) => cambia(i, { name: e.target.value })} />
          {v.label && <span className="muted eprof__voce-etichetta" title={v.label}>{v.label}</span>}
          <select aria-label={t('Accendi o spegni')} value={v.enabled == null ? '' : v.enabled ? 'on' : 'off'}
                  onChange={(e) => cambia(i, { enabled: e.target.value === '' ? null : e.target.value === 'on' })}>
            <option value="">{t('come la regola')}</option>
            <option value="on">{t('accendi')}</option>
            <option value="off">{t('spegni')}</option>
          </select>
          {v.enabled !== false && (
            <>
              <select aria-label={t('Frequenza')} value={v.interval_s ?? ''} onChange={(e) => cambia(i, { interval_s: e.target.value ? Number(e.target.value) : null })}>
                <option value="">{t('come la regola')}</option>
                {intervalli.map((s) => <option key={s} value={s}>{etichettaFrequenza(s)}</option>)}
              </select>
              <Soglia etichetta={t('attenzione')} valore={v.warn ?? 'default'} unit={unit} testoPredefinita={t('come la regola')} onCambia={(x) => cambia(i, { warn: x })} />
              <Soglia etichetta={t('critico')} valore={v.crit ?? 'default'} unit={unit} testoPredefinita={t('come la regola')} onCambia={(x) => cambia(i, { crit: x })} />
            </>
          )}
          <button type="button" className="secondary eprof__togli" aria-label={t('Togli la voce')} title={t('Togli la voce')}
                  onClick={() => onCambia(voci.filter((_, k) => k !== i))}>✕</button>
        </div>
      ))}
      <div className="eprof__voci-azioni">
        <button type="button" className="secondary" onClick={() => onCambia([...voci, { name: '' }])}>{t('Aggiungi una voce')}</button>
        <button type="button" className="secondary" onClick={() => setDaCi(true)}>{t('Prendi da un CI…')}</button>
        {voci.length === 0 && <span className="muted eagle-piccolo">{t('Nessuna voce: i nomi qui sopra (con *) valgono da soli; una voce ha le sue regolazioni.')}</span>}
      </div>
      {daCi && (
        <SceltaIstanze kind={kind} onChiudi={() => setDaCi(false)} onScegli={(nuove) => {
          const presenti = new Set(voci.map((v) => v.name.toLowerCase()))
          onCambia([...voci, ...nuove.filter((v) => !presenti.has(v.name.toLowerCase()))])
          setDaCi(false)
        }} />
      )}
    </div>
  )
}

/** A CI and the parameters of one kind it has: tick the ones to become rows. */
function SceltaIstanze({ kind, onChiudi, onScegli }: { kind: string; onChiudi: () => void; onScegli: (v: EagleVoceRegola[]) => void }) {
  const cis = useAsync(() => api.eagleCis(), [])
  const [ciId, setCiId] = useState('')
  const [cerca, setCerca] = useState('')
  const [parametri, setParametri] = useState<{ name: string; label: string; detail: string; available: boolean }[]>([])
  const [scelti, setScelti] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!ciId) { setParametri([]); return }
    let vivo = true
    void api.ciEagle(ciId).then((d) => {
      if (!vivo) return
      setParametri((d.params ?? []).filter((p) => p.kind === kind).map((p) => ({
        name: p.name || p.key, label: (p.detail ?? '').split(' · ')[0] ?? '', detail: p.detail ?? '', available: p.available,
      })))
      setScelti(new Set())
    }).catch(() => { if (vivo) setParametri([]) })
    return () => { vivo = false }
  }, [ciId, kind])
  const filtro = cerca.trim().toLowerCase()
  const elenco = cis.data?.licensed ? cis.data.cis : []
  const visibili = useMemo(() => elenco.filter((c: EagleMonitorato) =>
    !filtro || [c.name, c.mgmt_ip ?? '', c.ci_class ?? ''].some((x) => x.toLowerCase().includes(filtro))), [elenco, filtro])
  return (
    <Finestra titolo={t('Prendi le voci da un CI')} sotto={t('Un CI monitorato e i suoi {tipo}: le voci scelte entrano nella regola con il nome (e la descrizione) presi dal CI.', { tipo: etichettaKind(kind).toLowerCase() })} onChiudi={onChiudi}>
      <div className="form-dialog">
        <div className="form-grid">
          <label className="field span-3">
            <span className="field__label">{t('CI')}</span>
            <input type="search" value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder={t('cerca per nome, indirizzo o classe…')} aria-label={t('Cerca un CI')} />
            <select value={ciId} onChange={(e) => setCiId(e.target.value)} size={Math.min(8, Math.max(2, visibili.length + 1))}>
              <option value="">—</option>
              {visibili.map((c: EagleMonitorato) => <option key={c.ci_id} value={c.ci_id}>{c.name}{c.mgmt_ip ? ` (${c.mgmt_ip})` : ''}</option>)}
            </select>
          </label>
          <div className="field span-3">
            <span className="field__label">{t('Voci')}</span>
            {!ciId ? <span className="muted">{t('Scegli un CI.')}</span>
              : parametri.length === 0 ? <span className="muted">{t('Questo CI non ha parametri di questo tipo.')}</span>
                : (
                  <div className="eprof__scelta">
                    {parametri.map((p) => (
                      <label key={p.name} className={p.available ? undefined : 'muted'}>
                        <input type="checkbox" checked={scelti.has(p.name)} onChange={(e) => {
                          const dopo = new Set(scelti)
                          if (e.target.checked) dopo.add(p.name); else dopo.delete(p.name)
                          setScelti(dopo)
                        }} />{' '}
                        <strong>{p.name}</strong>{p.detail && <span className="muted"> — {p.detail}</span>}
                      </label>
                    ))}
                  </div>
                )}
          </div>
        </div>
        <div className="eagle-form__azioni">
          <button type="button" disabled={scelti.size === 0}
                  onClick={() => onScegli(parametri.filter((p) => scelti.has(p.name)).map((p) => ({ name: p.name, label: p.label })))}>
            {t('Aggiungi {n} voci', { n: scelti.size })}
          </button>
          <button type="button" className="secondary" onClick={onChiudi}>{t('Annulla')}</button>
        </div>
      </div>
    </Finestra>
  )
}

// ---------------------------------------------- the ad-hoc application

/**
 * «Parametri…» from the CI list selection: rules at once on the selected
 * CIs, merged with what they have or replacing it, and saved as a profile if
 * a name is given.
 */
export function FinestraParametri({ ids, onChiudi, onFatto }: { ids: string[]; onChiudi: () => void; onFatto: (testo: string) => void }) {
  const profili = useAsync(() => api.eagleProfili(), [])
  const [regole, setRegole] = useState<EagleRegolaProfilo[]>([{ ...REGOLA_VUOTA }])
  const [replace, setReplace] = useState(false)
  const [salvaCome, setSalvaCome] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  async function applica(e: FormEvent) {
    e.preventDefault()
    setErrore(null)
    setLavoro(true)
    try {
      const esito = await applicaInLotti(ids, { rules: regole, replace, save_as: salvaCome.trim() || null })
      onFatto(testoEsito(esito))
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }
  return (
    <Finestra titolo={t('Parametri di monitoraggio per {n} CI', { n: ids.length })} sotto={t('Le regole valgono per tipo di parametro: ogni CI prende quelli che ha e sa leggere.')} onChiudi={onChiudi}>
      <form onSubmit={applica} className="form-dialog">
        <EditorRegole regole={regole} onCambia={setRegole} intervalli={profili.data?.intervals ?? [60, 120, 300, 600, 900, 1800, 3600]} />
        <label className="checkbox"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />{' '}{t('Spegni ciò che le regole non nominano')}</label>
        <label className="field"><span className="field__label">{t('Salva anche come profilo (nome, facoltativo)')}</span>
          <input type="text" value={salvaCome} onChange={(e) => setSalvaCome(e.target.value)} placeholder={t('es. Server web')} /></label>
        <ErrorBanner error={errore} />
        <div className="eagle-form__azioni">
          <button type="submit" disabled={lavoro || regole.length === 0}>{lavoro ? t('Applico…') : t('Applica a {n} CI', { n: ids.length })}</button>
          <button type="button" className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Annulla')}</button>
        </div>
      </form>
    </Finestra>
  )
}

// ------------------------------------------------------------- the page

const VUOTO: EagleProfiloDef = {
  name: '', description: '', enabled: true, rules: [{ ...REGOLA_VUOTA }], replace: false,
  tag_key: null, tag_value: null, priority: 100, notify_group_id: null, sla_id: null,
}

export function EagleProfili({ puoModificare }: { puoModificare: boolean }) {
  const elenco = useAsync(() => api.eagleProfili(), [])
  const notifiche = useAsync(() => api.eagleNotify(), [])
  const [aperto, setAperto] = useState<EagleProfilo | 'nuovo' | null>(null)
  const [form, setForm] = useState<EagleProfiloDef>(VUOTO)
  const [tag, setTag] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [invio, setInvio] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)

  if (elenco.loading && !elenco.data) return <Spinner />
  if (!elenco.data?.licensed) {
    return <EmptyState title={t('Pagina non disponibile')} hint={t('Questa funzione non è attiva per il tuo spazio.')} />
  }
  const profili = elenco.data.profiles
  const intervalli = elenco.data.intervals
  const gruppi = notifiche.data?.groups ?? []
  const slas = notifiche.data?.slas ?? []

  async function esegui(fn: () => Promise<void>, etichetta: string): Promise<boolean> {
    setInvio(etichetta)
    setErrore(null)
    try {
      await fn()
      return true
    } catch (e) {
      setErrore(e)
      return false
    } finally {
      setInvio(null)
    }
  }
  function apri(p?: EagleProfilo) {
    if (p) {
      setForm({ name: p.name, description: p.description, enabled: p.enabled, rules: p.rules.map((r) => ({ ...r })), replace: p.replace,
                tag_key: p.tag_key, tag_value: p.tag_value, priority: p.priority, notify_group_id: p.notify_group_id, sla_id: p.sla_id })
      setTag(p.tag_key ? (p.tag_value ? `${p.tag_key}:${p.tag_value}` : p.tag_key) : '')
    } else {
      setForm({ ...VUOTO, rules: [{ ...REGOLA_VUOTA }] })
      setTag('')
    }
    setEsito(null)
    setAperto(p ?? 'nuovo')
  }
  async function salva(e: FormEvent) {
    e.preventDefault()
    const [chiave, ...resto] = tag.trim().split(':')
    const corpo: EagleProfiloDef = { ...form, tag_key: chiave.trim() || null, tag_value: resto.join(':').trim() || null }
    const ok = await esegui(async () => {
      if (aperto === 'nuovo' || !aperto) await api.eagleProfiloCrea(corpo)
      else await api.eagleProfiloAggiorna(aperto.id, corpo)
      await elenco.reload()
    }, 'salva')
    if (ok) setAperto(null)
  }
  async function applicaAdesso(p: EagleProfilo) {
    await esegui(async () => {
      const r = await api.eagleProfiloApplica({ profile_id: p.id })
      setEsito(`${p.name}: ${testoEsito(r)}`)
      await elenco.reload()
    }, `applica:${p.id}`)
  }
  async function elimina(p: EagleProfilo) {
    if (!window.confirm(t('Togliere il profilo di monitoraggio «{nome}»? Non si può annullare.', { nome: p.name }))) return
    await esegui(async () => {
      await api.eagleProfiloElimina(p.id)
      await elenco.reload()
    }, `elimina:${p.id}`)
  }
  const nomeGruppo = (id: string | null) => (id ? gruppi.find((g) => g.id === id)?.name ?? '?' : '')
  const nomeSla = (id: string | null) => (id ? slas.find((s) => s.id === id)?.name ?? '?' : '')

  return (
    <Card title={t('Profili di monitoraggio ({n})', { n: profili.length })}
          actions={puoModificare && !aperto ? <button type="button" className="secondary" onClick={() => apri()}>{t('Nuovo profilo')}</button> : undefined}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Un profilo è un elenco di regole per tipo di parametro — ping, CPU, interfacce, servizi per nome… — con soglie e frequenza. Si applica a più CI insieme dall’elenco dei Configuration Item («Applica profilo…») o da solo ai CI che portano un tag, ogni dieci minuti.')}
      </p>
      <ErrorBanner error={errore} />
      {esito && <p className="muted eprof__esito">{esito}</p>}
      {aperto && (
        <form onSubmit={salva} className="eagle-form">
          <div className="form-grid">
            <label className="field span-3"><span className="field__label">{t('Nome')}</span>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
            <label className="field span-3"><span className="field__label">{t('Descrizione')}</span>
              <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <label className="field span-3"><span className="field__label">{t('Tag che lo applica da solo (chiave:valore, o solo chiave)')}</span>
              <input type="text" value={tag} onChange={(e) => setTag(e.target.value)} placeholder={t('es. ruolo:core-switch')} /></label>
            <label className="field span-1"><span className="field__label">{t('Priorità')}</span>
              <input type="number" min={0} max={1000} value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} /></label>
            <label className="field"><span className="field__label">{t('Gruppo di notifica')}</span>
              <select value={form.notify_group_id ?? ''} onChange={(e) => setForm({ ...form, notify_group_id: e.target.value || null })}>
                <option value="">{t('non cambiare')}</option>
                {gruppi.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select></label>
            <label className="field"><span className="field__label">{t('SLA')}</span>
              <select value={form.sla_id ?? ''} onChange={(e) => setForm({ ...form, sla_id: e.target.value || null })}>
                <option value="">{t('non cambiare')}</option>
                {slas.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></label>
          </div>
          <fieldset className="field"><legend className="field__label">{t('Regole')}</legend>
            <EditorRegole regole={form.rules} onCambia={(rules) => setForm({ ...form, rules })} intervalli={intervalli} />
          </fieldset>
          <label className="eagle-interruttore"><input type="checkbox" checked={form.replace} onChange={(e) => setForm({ ...form, replace: e.target.checked })} />{' '}{t('Spegni ciò che il profilo non nomina (altrimenti il resto resta com’è)')}</label>
          <br />
          <label className="eagle-interruttore"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />{' '}{t('Attivo')}</label>
          <div className="eagle-form__azioni">
            <button type="submit" disabled={invio === 'salva' || form.rules.length === 0}>{invio === 'salva' ? t('Salvo…') : t('Salva')}</button>
            <button type="button" className="secondary" onClick={() => setAperto(null)} disabled={!!invio}>{t('Annulla')}</button>
          </div>
        </form>
      )}
      {profili.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>{t('Nessun profilo ancora: creane uno, poi applicalo dall’elenco dei CI o legalo a un tag.')}</p>
      ) : (
        <DataTable
          rows={profili}
          rowKey={(p) => p.id}
          rowClassName={(p) => (p.enabled ? undefined : 'eagle-riga--spenta')}
          conteggio={{ nome: t('profili') }}
          columns={[
            {
              key: 'name', header: t('Nome'), value: (p) => p.name,
              render: (p) => <><strong>{p.name}</strong>{!p.enabled && <span className="muted"> · {t('spento')}</span>}{p.description && <div className="muted eprof__riassunto">{p.description}</div>}</>,
            },
            { key: 'rules', header: t('Regole'), value: (p) => riassuntoRegole(p.rules), render: (p) => <span className="eprof__riassunto">{riassuntoRegole(p.rules)}{p.replace && <span className="muted"> · {t('spegne il resto')}</span>}</span> },
            {
              key: 'tag', header: t('Tag'), value: (p) => (p.tag_key ? (p.tag_value ? `${p.tag_key}:${p.tag_value}` : p.tag_key) : ''),
              render: (p) => (p.tag_key ? <span className="mono">{p.tag_value ? `${p.tag_key}:${p.tag_value}` : p.tag_key}</span> : <span className="muted">—</span>),
            },
            { key: 'priority', header: t('Priorità'), value: (p) => p.priority },
            { key: 'notify', header: t('Notifica · SLA'), value: (p) => [nomeGruppo(p.notify_group_id), nomeSla(p.sla_id)].filter(Boolean).join(' · ') },
            { key: 'cis', header: t('CI'), value: (p) => p.cis },
            {
              key: 'applied', header: t('Ultima applicazione'), value: (p) => p.last_applied_at ?? '',
              render: (p) => (p.last_applied_at ? <>{formatDateTime(p.last_applied_at)}<span className="muted"> · {t('{n} CI col tag', { n: p.last_matched })}</span></> : <span className="muted">—</span>),
            },
            ...(puoModificare ? [{
              key: 'actions', header: '', sort: false as const, filter: false as const, searchable: false as const, value: () => '',
              render: (p: EagleProfilo) => (
                <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="secondary" onClick={() => apri(p)} disabled={!!invio}>{t('Modifica')}</button>{' '}
                  {p.tag_key && <><button type="button" className="secondary" onClick={() => void applicaAdesso(p)} disabled={!!invio}>{invio === `applica:${p.id}` ? t('Applico…') : t('Applica adesso')}</button>{' '}</>}
                  <button type="button" className="secondary" onClick={() => void elimina(p)} disabled={!!invio}>{t('Togli')}</button>
                </span>
              ),
            }] : []),
          ]}
        />
      )}
    </Card>
  )
}
